import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { RatedReply, TurnRating } from '@jabberr/core';
import { BackIcon } from '../components/icons.js';
import { EmptyState } from '../components/EmptyState.js';
import { Markdown } from '../components/Markdown.js';
import { ErrorBox, Spinner } from '../components/ui.js';
import { api, errorMessage } from '../lib/api.js';
import { conversationTitle, relativeTime } from '../lib/format.js';
import { roleLabel } from '../lib/plain.js';
import { useDrawer } from '../state/drawer.js';

/**
 * Replies people rated, newest first: what was asked, what Jabberr said, and which role and prompt version said it.
 * Copy as smoke case gives the docs/smoke/scenarios.json entry; a person adds must and fails-if before committing it.
 */
export function RatedRepliesPage() {
  const { projectId = '' } = useParams();
  const { setInfo } = useDrawer();
  const [rating, setRating] = useState<TurnRating>('down');
  const [replies, setReplies] = useState<RatedReply[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    setReplies(null);
    setError(null);
    setInfo({ projectId, title: 'Rated replies' });
    api.outputs
      .rated(projectId, rating)
      .then((r) => setReplies(r.replies))
      .catch((err) => setError(errorMessage(err)));
  }, [projectId, rating, setInfo]);

  const copy = (reply: RatedReply) => {
    void navigator.clipboard?.writeText(JSON.stringify(reply.smokeCase, null, 2)).then(() => setCopied(reply.turnId));
  };

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <Link to={`/p/${projectId}/knows`} className="back-link">
            <BackIcon />
            What Jabberr knows
          </Link>
          <h1>Rated replies</h1>
        </div>
      </header>
      <p className="page-subtitle muted small">
        Replies people rated with a thumb. Read what was asked and what Jabberr said; copy a missed one as a smoke case, add what it must and must
        not do, and commit it to docs/smoke/scenarios.json.
      </p>
      <div className="rated-filter" role="group" aria-label="Rating">
        <button type="button" className={`btn ${rating === 'down' ? 'btn-primary' : 'btn-secondary'}`} aria-pressed={rating === 'down'} onClick={() => setRating('down')}>
          Thumbs down
        </button>
        <button type="button" className={`btn ${rating === 'up' ? 'btn-primary' : 'btn-secondary'}`} aria-pressed={rating === 'up'} onClick={() => setRating('up')}>
          Thumbs up
        </button>
      </div>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      {replies === null && !error ? (
        <Spinner label="Loading" />
      ) : replies && replies.length === 0 ? (
        <EmptyState title={rating === 'down' ? 'No thumbs down yet.' : 'No thumbs up yet.'} />
      ) : (
        <ul className="list rated-list">
          {(replies ?? []).map((r) => (
            <li key={r.turnId} className="list-item rated-row">
              <div className="rated-meta muted small">
                <Link to={`/p/${projectId}/c/${r.conversationId}`}>{conversationTitle(r.conversationTitle)}</Link>
                {' · '}
                {roleLabel(r.role)}
                {r.rolePromptVersion !== null ? ` v${r.rolePromptVersion}` : ''}
                {' · context v'}
                {r.contextVersion}
                {r.modality === 'voice' ? ' · voice' : ''}
                {' · '}
                {relativeTime(r.createdAt)}
              </div>
              <p className="rated-prompt">{r.prompt || 'No typed question before this reply.'}</p>
              <details className="rated-reply">
                <summary>Jabberr's reply</summary>
                <Markdown text={r.reply} />
              </details>
              <div className="rated-actions">
                <button type="button" className="btn btn-secondary" onClick={() => copy(r)}>
                  {copied === r.turnId ? 'Copied' : 'Copy as smoke case'}
                </button>
                {r.requestId ? <span className="muted small">Request {r.requestId}</span> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
